import { useRef, useState } from 'react'
import { api } from '../../../lib/api.js'
import { uploadOwnedFile } from '../ownedAsset.js'
import SchoolPicker from '../SchoolPicker.jsx'
import GptBridgePanel from '../gptBridge/GptBridgePanel.jsx'
import { provenanceLine } from '../gptBridge/bridgeModel.js'
import MarkRung from '../MarkRung.jsx'
import PlaceRealMark from '../composite/PlaceRealMark.jsx'
import {
  MARK_ROUTES, bezelDropPrompt, shankState, BEZEL_DROP_HONESTY, BEZEL_DROP_SHEET,
} from './bezelDrop.js'
import styles from './BezelDrop.module.css'

/**
 * LEVEL 3 · BEZEL DROP — one mark, every housing, one sheet.
 *
 * Brian went looking for this in the LEVEL control on 2026-09-18, did not find
 * it, and said *"it must have the THIRD WORKING level"*. Working means he can
 * finish the job on this screen, not that a third chip exists.
 *
 * ⚠️ THIS IS NOT THE THIRTEEN-STEP WIZARD AND MUST NOT BECOME IT. A Bezel Drop
 * is not a ring build: there is no bezel to pick, no shank library to choose
 * from, no metal, no stones, no keyed title. There is ONE input that matters —
 * the client's mark — and everything else on this screen exists to get that
 * mark to the GPT and the sheet back into HubSpot File Manager.
 *
 * ⚠️ THE MARK ROUTES ARE ORDERED BY MEASUREMENT, NOT BY TIDINESS. Upload is
 * first and it is a primary control. 0 of 335 sampled high schools carry an
 * owned crest (measured live 2026-09-14) and the mark finder found nothing for
 * 20 of 20 crest-less high schools, so a screen that leads with "the team's
 * owned mark" leads most reps into a dead end on most jobs. See MARK_ROUTES.
 */
export default function BezelDrop({ job = null, onArtifact } = {}) {
  const [school, setSchool] = useState(null)
  const [teamName, setTeamName] = useState(job?.spec?.schoolName || job?.title || '')
  const [mark, setMark] = useState(null)
  const [shankWords, setShankWords] = useState('')
  const [note, setNote] = useState('')
  /* ⚠️ OFF BY DEFAULT, and that is a deliberate choice rather than caution.
   * A reserved-plate sheet is not a finished sheet: it is the INPUT to the
   * composite. Until the composite runs in the product, a rep who turns this
   * on and emails the result has sent a page of blank rings. So the default
   * stays the thing that produces something showable, and this is the opt-in
   * for the route that produces something REAL. */
  const [reservePlate, setReservePlate] = useState(true)
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState({ tone: '', text: '' })
  const [sheet, setSheet] = useState(null)
  /* The rung 2 output, once the real mark has been placed on the sheet. */
  const [placed, setPlaced] = useState(null)
  const fileRef = useRef(null)

  const say = (tone, text) => setMsg({ tone, text })
  const name = String(teamName || school?.name || '').trim()

  /* THE PROMPT IS BUILT FROM THE SAME PURE FUNCTION THE COPY BUTTON SHOWS, so
   * the one-press bridge and the paste-it-by-hand path cannot drift. It is only
   * produced once a mark exists: sending this prompt with no reference attached
   * is an invitation for the model to draw a mark of its own, which is the one
   * outcome nobody wants on a sheet with a client's name near it. */
  const prompt = mark
    ? bezelDropPrompt({ teamName: name, markName: mark.name, shankWords, note, reservePlate })
    : ''

  /**
   * ⚠️ THE SCHOOL'S DISPLAY NAME IS NOT THE TEAM NAME. Walking the chain live
   * on 2026-09-18, picking East Carolina filled the field with
   * "East Carolina University Greenville, North Carolina" — the picker's label,
   * city and state included. That string then goes into the prompt as the name
   * the sheet is for, so the GPT is told the team is called "…Greenville, North
   * Carolina". The operator can fix it by hand, but only if they notice, and a
   * bad default that reads as filled-in is worse than an empty field.
   *
   * So the location tail is trimmed off. Anything after a comma is a place, and
   * a trailing city repeated from a `city`/`state` field goes too. The field
   * stays editable, because a team name is a human's call.
   */
  function teamNameFrom(s) {
    let n = String(s?.name || '').trim()
    if (!n) return ''
    n = n.split(',')[0].trim()
    for (const tail of [s?.city, s?.state, s?.location].filter(Boolean)) {
      const re = new RegExp('\\s+' + String(tail).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i')
      n = n.replace(re, '').trim()
    }
    return n
  }

  function chooseSchool(s) {
    setSchool(s)
    setTeamName(teamNameFrom(s))
    setMark(null)
    say('', '')
  }

  /* ROUTE 1 · the attachment. Owned on arrival: the file goes through the same
   * upload route the crest uses, so what the GPT is shown is a file SR hosts
   * rather than something on the operator's desktop. */
  async function attachMark(file) {
    if (!file) return
    setBusy('manual')
    say('', '')
    try {
      const owned = await uploadOwnedFile(file, { kind: 'crest' })
      setMark({ url: owned.url, name: name ? `${name} mark` : file.name, route: 'manual' })
      onArtifact?.({ ...owned, role:'crest', name:file.name })
      say('ok', 'Owned in HubSpot File Manager and attached to this sheet as the reference the GPT is shown.')
    } catch (err) {
      setMark(null)
      say('warn', `${err?.message || 'The upload failed.'} Nothing was attached, so there is still no mark to send.`)
    } finally {
      setBusy('')
    }
  }

  /* ROUTE 2 · the primary owned mark, straight off the school record. */
  function usePrimary() {
    if (!school?.logoUrl) return
    setMark({ url: school.logoUrl, name: `${school.name} primary mark`, route: 'primary' })
    onArtifact?.({url:school.logoUrl,role:'crest',name:`${school.name} crest.png`})
    say('ok', 'Using the owned mark already on the school record.')
  }

  /* ROUTE 3 · the mark finder. It is a SECOND owned route, not a better one:
   * outside college it usually answers with nothing, and this says so rather
   * than spinning and then going quiet. */
  async function useSecondary() {
    if (!name) return
    setBusy('secondary')
    say('', '')
    try {
      const res = await api.logoFind({ name })
      if (res?.found && res?.logo) {
        const same = school?.logoUrl && res.logo === school.logoUrl
        setMark({ url: res.logo, name: `${res.matched || name} ${same ? 'primary' : 'secondary'} mark`, route: 'secondary' })
        say(same ? 'warn' : 'ok', same
          ? 'The finder returned the SAME file as the primary mark, so there is no separate secondary on file. It is attached, but it is the primary.'
          : res.owned
            ? 'A second owned mark was on file. Attached.'
            : 'Found and staged as an owned copy — it goes to the Marks Library queue for approval.')
      } else {
        say('warn', `No second owned mark on file for “${name}”. That is the usual answer outside college — attach the mark above instead.`)
      }
    } catch (err) {
      say('warn', `The mark finder is unavailable (${err?.status || 'network error'}). Attach the mark above instead.`)
    } finally {
      setBusy('')
    }
  }

  const shank = shankState(shankWords)

  return (
    <div className={styles.wrap}>
      {/* ⚠️ THE HONEST LABEL IS THE FIRST THING ON THE SCREEN, not a footnote
          under the result. By the time the sheet exists the operator has
          already decided what they think it is. */}
      <section className={`card ${styles.honesty}`}>
        <div className="field-label">Drawn by the model — read this first</div>
        <p className={styles.headline}>{BEZEL_DROP_HONESTY.headline}</p>
        <p className={styles.note}>{BEZEL_DROP_HONESTY.body}</p>
        <p className={styles.note}>{BEZEL_DROP_HONESTY.gate}</p>
        <p className={styles.note}>{BEZEL_DROP_HONESTY.check}</p>
      </section>

      <section className={`card ${styles.block}`}>
        <div className="field-label">The team</div>
        <p className={styles.note}>
          Names the sheet and gives the owned-mark routes something to look up. A Bezel Drop for a prospect that is not
          on the records yet needs no school at all — type the name and attach the mark.
        </p>
        <SchoolPicker onSelect={chooseSchool} selectedId={school?.id} />
        <input
          className="input"
          placeholder="Team name as it should read"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
        />
      </section>

      <section className={`card ${styles.block}`}>
        <div className="field-label">The mark — three ways, and attaching is the one that always works</div>

        <div className={styles.routeActions}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy === 'manual' ? 'Uploading…' : 'Attach the mark from this device'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            onChange={(e) => { attachMark(e.target.files?.[0]); e.target.value = '' }}
          />
          <button
            type="button"
            className="btn"
            disabled={!!busy || !school?.logoUrl}
            onClick={usePrimary}
          >
            Use the primary owned mark
          </button>
          <button
            type="button"
            className="btn"
            disabled={!!busy || !name}
            onClick={useSecondary}
          >
            {busy === 'secondary' ? 'Searching…' : 'Find a secondary owned mark'}
          </button>
        </div>

        {school && !school.logoUrl && (
          <p className={styles.note}>
            No owned mark on the record for {school.name}, so the primary route has nothing to offer. Attach the mark
            instead — that is the expected answer for a high school, not a fault.
          </p>
        )}

        <ul className={styles.routes}>
          {MARK_ROUTES.map((r) => (
            <li key={r.id} className={mark?.route === r.id ? `${styles.route} ${styles.routeOn}` : styles.route}>
              <strong>{r.label}</strong>
              <span>{r.what}</span>
              <span className={styles.measured}>{r.measured}</span>
            </li>
          ))}
        </ul>

        {mark && (
          <div className={styles.chosen}>
            <img src={mark.url} alt="" className={styles.thumb} />
            <div>
              <strong>{mark.name}</strong>
              <span className={styles.note}>
                Owned file, attached to every run as the reference. The GPT draws its own version of it.
              </span>
            </div>
          </div>
        )}

        {msg.text && <p className={msg.tone === 'ok' ? styles.ok : styles.warn} role="status">{msg.text}</p>}
      </section>

      <section className={`card ${styles.block}`}>
        <div className="field-label">Shank wording</div>
        {/* ⚠️ MEASURED FLAW, HANDLED RATHER THAN HOPED ABOUT. Given nothing, the
            GPT wrote PIRATES, DISCIPLINE and FAMILY onto the shank panels on its
            own (live run, 2026-09-18) — words nobody supplied, on a sheet with a
            client's name on it. So there is no silent state: type the wording,
            or the prompt orders every panel left blank. */}
        <p className={styles.note}>
          Leave this empty and the prompt ORDERS every shank panel blank. That is deliberate: given no wording the GPT
          invents its own — a live run produced PIRATES, DISCIPLINE and FAMILY unprompted. Type the words only if the
          client has actually given them to you.
        </p>
        <textarea
          className="input"
          rows={2}
          placeholder="One per line, or comma separated. Leave empty for blank panels."
          value={shankWords}
          onChange={(e) => setShankWords(e.target.value)}
        />
        <p className={styles.state}>
          {shank === 'blank'
            ? 'Panels will be ordered BLANK.'
            : 'Panels will carry exactly what you typed, and nothing else.'}
        </p>
      </section>

      <section className={`card ${styles.block}`}>
        <div className="field-label">The mark plate</div>
        {/* ⚠️ THE MEASUREMENT IS ON SCREEN, because this control decides whether
            the sheet carries a drawn approximation or the real file. */}
        <label className={styles.reserveRow}>
          <input
            type="checkbox"
            checked={reservePlate}
            onChange={(e) => setReservePlate(e.target.checked)}
          />
          <span>Leave every plate EMPTY, for the real crest to be placed onto</span>
        </label>
        <p className={styles.note}>
          {reservePlate
            ? 'The sheet comes back with blank polished plates and no mark on any housing. That is the input to the composite, not a sheet to send: on its own it is a page of unmarked rings.'
            : 'The GPT draws its own approximation of the mark onto every housing. Quick, and it is rung 1, a sketch.'}
        </p>
        <p className={styles.note}>
          Measured 2026-09-18. With the mark drawn on, plate detection found <strong>0 of 19</strong> usable plates.
          With plates reserved, it found <strong>21 of 21</strong>. That is what makes placing the genuine crest possible.
        </p>
      </section>

      <section className={`card ${styles.block}`}>
        <div className="field-label">Anything else for this sheet (optional)</div>
        <input
          className="input"
          placeholder="A note added to the end of the prompt"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className={styles.note}>
          The sheet itself is fixed: {BEZEL_DROP_SHEET.orientation}, {BEZEL_DROP_SHEET.field}, about{' '}
          {BEZEL_DROP_SHEET.housings} housings in {BEZEL_DROP_SHEET.rows} rows, every piece straight on to the camera,
          the SR hexagon and line top left, the Southern Recognition Inc watermark faded behind. That is the product, so
          it is not a set of toggles.
        </p>
      </section>

      {/* THE BRIDGE, UNCHANGED AND PROVEN — pointed at the Bezel Drop GPT, with
          its manual round trip permanently on screen. `requireBuild={false}`
          because a sheet is not a build: there is no record to hang it on, and
          the file name carries the provenance instead. */}
      <GptBridgePanel
        buildId=""
        view="bezel-drop"
        viewLabel="the Bezel Drop sheet"
        prompt={prompt}
        crestUrl={mark?.url || ''}
        target="bezelDropGpt"
        requireBuild={false}
        eyebrow="Bezel Drop · rendered in your GPT, no API spend"
        onAttached={(image) => { setSheet(image); onArtifact?.({ ...image, role:'sheet', name:'Bezel Drop concept.png' }) }}
      />

      {sheet && (
        <section className={`card ${styles.block}`}>
          <div className="field-label">The sheet</div>
          <a href={sheet.url} target="_blank" rel="noopener noreferrer">
            <img src={sheet.url} alt="The Bezel Drop sheet" className={styles.sheet} />
          </a>
          {/* ⚠️ THE RUNG, NOT JUST THE PROVENANCE SENTENCE. A GPT sheet is rung
              1 of 3 and the badge says so in the words somebody would use,
              beside a plain verdict on whether it may go to a customer. The
              sentence below it stays, because it carries the file's own
              provenance; the badge carries the decision. */}
          <MarkRung markSource={placed ? placed.markSource : sheet.mark} detail={provenanceLine(sheet)} />
          <p className={styles.warn}>{BEZEL_DROP_HONESTY.check}</p>
        </section>
      )}

      {/* ⚠️ RUNG 2, REACHABLE. It only appears once there is a sheet AND an
          owned mark to place, because without both it is a button that cannot
          work. The sheet it wants is the reserved-plate one: on a sheet with
          the mark already drawn on, plate detection finds nothing and the panel
          says exactly that rather than failing silently. */}
      {sheet && mark && (
        <PlaceRealMark
          sheetUrl={sheet.url}
          markUrl={mark.url}
          teamName={name}
          onPlaced={(image) => { setPlaced(image); onArtifact?.({ ...image, role:'sheet', name:'Bezel Drop with placed crest.png' }) }}
        />
      )}
    </div>
  )
}
